import { useCallback, useState } from 'react';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import { ButtonType } from '../../../../types/button-type';
import IconCycle from '../../../../components/Icons/IconCycle/IconCycle';
import IconEditOutlined from '../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import IconPlus from '../../../../components/Icons/IconPlus/IconPlus';
import IconBin from '../../../../components/Icons/IconBin/IconBin';
import ScreenCyclesTable from '../../../../components/ScreenCyclesTable/ScreenCyclesTable';
import { ICyclesScreenInteractorProps, useCyclesScreenInteractor } from './interactor';
import CycleStatus from '../../../../enums/cycle-status.enum';
import Modal from '../../../../components/Modal/Modal';
import CycleSearchScreen from '../../../../components/CycleSearchScreen/CycleSearchScreen';
import SearchScreen from '../../../../components/SearchScreen/SearchScreen';
import CycleSettings from '../../../../components/CycleSettings/CycleSettings';
import { DataLoadingWrapper } from '../../components/DataLoadingWrapper';
import CriticalDeviation from '../../../../components/CriticalDeviation';
import { ElementSize } from '../../../../types/element-size';
import CycleAttachment from '../../../../components/CycleAttachment/CycleAttachment';
import DuplicateCycleModal from '../../../../components/DuplicateCycleModal/DuplicateCycleModal';
import { useCycleDropTarget } from '../../../../hooks/useCycleDropTarget';

export interface ICyclesScreenProps extends ICyclesScreenInteractorProps {
  name: string;
  criticalDeviation: number;
}

export const CyclesScreen = ({ name, criticalDeviation, screenId, ...payload }: ICyclesScreenProps) => {
  const {
    cycles,
    cyclesBySearch,
    isLoading,
    screenPath,
    editCyclePath,
    canShowCriticalDeviation,
    canOpenSearchModal,
    contractId,
    clientId,
    canOpenDeleteModal,
    addCycleToScreen,
    refetchCycleSource,
    deleteScreenHandler,
    getCyclesByContractId,
    updateCriticalDeviation,

    cycleActions,

    //cycle settings
    cycleIdSettings,
    cycleNameSettings,
    openSelectedCycleSettings,
    closeSelectedCycleSettings,

    //delete cycle from screen
    cycleIdDelete,
    closeSelectedCycleDelete,
    onAcceptSelectedCycleDelete,

    //cycle complete
    cycleIdComplete,
    closeSelectedCycleComplete,
    onAcceptSelectedCycleComplete,

    //cycle make inactive
    cycleIdMakeInactive,
    closeSelectedCycleMakeInactive,
    onAcceptSelectedCycleMakeInactive,

    //attach to cycle
    cycleIdAttach,
    closeSelectedCycleAttach,
    onAcceptSelectedCycleAttach,
    onSelectFile,
    isSaveAttachment,

    //change screen
    cycleIdChangeScreen,
    closeSelectedCycleChangeScreen,
    onChangeScreenToCycle,

    screensBySearch,
    getScreensByContractId,

    //multi-duplicate cycle
    cycleIdDuplicate,
    cycleNameDuplicate,
    closeSelectedCycleDuplicate,
    onCycleDuplicated,
  } = useCyclesScreenInteractor({
    ...payload,
    screenId,
  });

  const { isDragOver, dropZoneProps } = useCycleDropTarget(contractId, async (cycleId, sourceScreenId) => {
    await addCycleToScreen(cycleId);
    await refetchCycleSource(sourceScreenId);
  });

  const [isOpenSearch, setIsOpenSearch] = useState<boolean>(false);
  const [isOpenDeleteScreen, setIsOpenDeleteScreen] = useState<boolean>(false);

  const openDeleteScreenModal = () => {
    setIsOpenDeleteScreen(true);
  };

  const closeDeleteScreenModal = () => {
    setIsOpenDeleteScreen(false);
  };

  const acceptDeleteScreen = useCallback(() => {
    closeDeleteScreenModal();
    deleteScreenHandler();
  }, [deleteScreenHandler]);

  const openSearchModal = () => {
    setIsOpenSearch(true);
  };

  const closeSearchModal = () => {
    setIsOpenSearch(false);
  };

  return (
    <div
      className={`c-cycles-screen relative flex flex-auto flex-col overflow-hidden rounded-lg border bg-white p-3 shadow-15 transition-colors dark:bg-boxdark ${
        isDragOver ? 'border-primary bg-primary/10' : 'border-foggy-mist dark:border-strokedark'
      }`}
      {...dropZoneProps}
    >
      <div className="c-cycles-screen__header min-xsm:flex-row flex w-full items-center justify-between gap-x-2 pb-5 max-xsm:pb-2 max-xsm:flex-col 5 max-xsm:gap-y-1">
        <span className="c-cycles-screen__name truncate text-base font-bold md:text-xl">Cycle Screen: {name}</span>
        <div className="c-cycles-screen__actions flex flex-row max-xsm:w-full items-center gap-x-2 max-xsm:justify-between">
          {canShowCriticalDeviation && (
            <CriticalDeviation value={criticalDeviation} updateCriticalDeviation={updateCriticalDeviation} />
          )}
          {!!screenPath && (
            <InteractiveButton
              className="max-md:leading-none max-md:min-w-16 max-md:px-2 max-md:min-h-7 max-md:text-xs text-nowrap"
              type={ButtonType.PrimaryInverse}
              iconLeft={<IconCycle size={ElementSize.XXS} />}
              text="View screen"
              as="a"
              href={screenPath}
            />
          )}
          {!!editCyclePath && (
            <InteractiveButton
              className="max-md:leading-none max-md:min-w-16 max-md:px-2 max-md:min-h-7  max-md:text-xs text-nowrap"
              type={ButtonType.PrimaryInverse}
              iconLeft={<IconEditOutlined size={ElementSize.XXS} stroke="#EF8541" />}
              text="Edit"
              as="a"
              href={editCyclePath}
              target="_blank"
            />
          )}
          {canOpenSearchModal && (
            <InteractiveButton
              className="min-w-fit max-md:px-2 max-md:min-h-7 "
              type={ButtonType.PrimaryInverse}
              iconLeft={<IconPlus size={ElementSize.XXS} fill="#000" />}
              onClick={openSearchModal}
            />
          )}
          {canOpenDeleteModal && (
            <InteractiveButton
              className="max-md:leading-none max-md:min-w-16 max-md:px-2 max-md:text-xs text-nowrap max-md:min-h-7 "
              iconLeft={<IconBin size={ElementSize.XXS} fill="#fff" />}
              text="Delete"
              onClick={openDeleteScreenModal}
            />
          )}
        </div>
      </div>
      <DataLoadingWrapper dataExists={!!cycles.length} isLoading={isLoading}>
        <ScreenCyclesTable
          openSettings={openSelectedCycleSettings}
          screenCycles={cycles}
          actionsButtons={cycleActions}
          status={CycleStatus.Active}
          screenCriticalDeviation={criticalDeviation}
          dragContractId={contractId}
          dragScreenId={screenId}
        />
      </DataLoadingWrapper>
      <Modal
        modalTitle="Screen deletion"
        isVisible={isOpenDeleteScreen}
        content={`Are you sure you want to delete the ${name} screen?`}
        acceptBtnText="Delete"
        onClose={closeDeleteScreenModal}
        onAccept={acceptDeleteScreen}
        showAcceptBtn
        showCancelBtn
      />
      <Modal
        modalTitle="Cycle deletion from screen"
        isVisible={!!cycleIdDelete}
        content="Are you sure you want to delete this cycle from screen?"
        acceptBtnText="Remove"
        onClose={closeSelectedCycleDelete}
        onAccept={onAcceptSelectedCycleDelete}
        showAcceptBtn
        showCancelBtn
      />
      <Modal
        modalTitle="Complete Cycle"
        isVisible={!!cycleIdComplete}
        content="Are you sure you want to complete this cycle?"
        acceptBtnText="Complete"
        onClose={closeSelectedCycleComplete}
        onAccept={onAcceptSelectedCycleComplete}
        showAcceptBtn
        showCancelBtn
      />
      <Modal
        modalTitle="Make Cycle Inactive"
        isVisible={!!cycleIdMakeInactive}
        content="Are you sure you want to move this cycle back to inactive?"
        acceptBtnText="Make Inactive"
        onClose={closeSelectedCycleMakeInactive}
        onAccept={onAcceptSelectedCycleMakeInactive}
        showAcceptBtn
        showCancelBtn
      />
      <Modal
        modalTitle="Cycle Attachments"
        isVisible={!!cycleIdAttach}
        onClose={closeSelectedCycleAttach}
        onAccept={onAcceptSelectedCycleAttach}
        acceptBtnText={`${isSaveAttachment ? 'Saving...' : 'Save'}`}
        className="min-h-[500px] w-[800px]"
      >
        <CycleAttachment onSelectFile={onSelectFile} />
      </Modal>
      {!!cycleIdSettings && (
        <CycleSettings isVisible={!!cycleIdSettings} onClose={closeSelectedCycleSettings} cycleId={cycleIdSettings} cycleName={cycleNameSettings} />
      )}

      {!!isOpenSearch && (
        <CycleSearchScreen
          visibility={isOpenSearch}
          onClose={closeSearchModal}
          addNewCycle={addCycleToScreen}
          getCyclesByContractId={getCyclesByContractId}
          cyclesBySearch={cyclesBySearch}
          screenId={screenId}
          clientId={clientId}
          contractId={contractId}
        />
      )}

      {!!cycleIdChangeScreen && (
        <SearchScreen
          visibility={!!cycleIdChangeScreen}
          onClose={closeSelectedCycleChangeScreen}
          getScreenByContractId={(payload: { query?: any }) =>
            getScreensByContractId({ contractId, ...payload })
          }
          screensBySearch={screensBySearch}
          attachScreen={onChangeScreenToCycle}
          showTransferOption={true}
          currentScreenId={screenId}
          transferScreen={onChangeScreenToCycle}
        />
      )}

      <DuplicateCycleModal
        cycleId={cycleIdDuplicate}
        cycleName={cycleNameDuplicate}
        isVisible={!!cycleIdDuplicate}
        onClose={closeSelectedCycleDuplicate}
        onDuplicated={onCycleDuplicated}
      />
    </div>
  );
};
